import { useState } from "react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { SITE } from "@/config/site";
import { liveJobs } from "@/config/jobs";
import { Briefcase, MapPin, CalendarBlank, CaretDown, EnvelopeSimple, WhatsappLogo } from "@phosphor-icons/react";

export default function Careers() {
  const jobs = liveJobs();
  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <PageMeta title="Careers — Ascentremit" description="Open roles at Ascentremit. Help every child start term on time." />
      <PublicNav />
      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-16 py-14 lg:py-20" data-testid="careers-page">
        <div className="inline-flex items-center gap-2 overline mb-4"><Briefcase size={14} weight="fill" /> Careers</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#132A13] font-medium leading-tight">Help every child start term on time.</h1>
        <p className="mt-6 text-base text-[#4F5D4F] leading-relaxed">We're a small team building fair, clearly priced credit for Nigerian families and academic staff.</p>
        <div className="mt-12 space-y-5">
          {jobs.length === 0 ? (
            <div className="rounded-2xl bg-white border border-[#E5E3DB] p-6 text-sm text-[#4F5D4F]" data-testid="careers-empty">
              There are no open roles right now. Send your CV to <a href={`mailto:${SITE.SUPPORT_EMAIL}`} className="text-[#0A3622] underline">{SITE.SUPPORT_EMAIL}</a> and we'll be in touch when something fits.
            </div>
          ) : jobs.map((j) => <JobCard key={j.slug} job={j} />)}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function JobCard({ job }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="rounded-2xl bg-white border border-[#E5E3DB]" data-testid={`job-${job.slug}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-6 flex items-start justify-between gap-4" data-testid={`job-toggle-${job.slug}`}>
        <div>
          <h2 className="font-display text-2xl font-medium text-[#132A13]">{job.title}</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#4F5D4F]">
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} weight="fill" /> {job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase size={14} weight="fill" /> {job.type}</span>
            <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} weight="fill" /> Applications close {job.closes}</span>
          </div>
        </div>
        <CaretDown size={20} className={`shrink-0 mt-2 text-[#0A3622] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-[#F0EFEA] pt-5 space-y-6" data-testid={`job-details-${job.slug}`}>
          {job.sections.map((s) => (
            <div key={s.h}>
              <h3 className="font-display text-lg font-medium text-[#132A13]">{s.h}</h3>
              {s.p?.map((t) => <p key={t} className="text-sm text-[#4F5D4F] leading-relaxed mt-2">{t}</p>)}
              {s.li && <ul className="mt-2 space-y-1.5 list-disc pl-5 text-sm text-[#4F5D4F] leading-relaxed">{s.li.map((t) => <li key={t}>{t}</li>)}</ul>}
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <a href={`mailto:hello@ascentremit.com?subject=${encodeURIComponent(job.apply_subject)}`} className="btn-primary" data-testid={`job-apply-${job.slug}`}><EnvelopeSimple size={16} weight="fill" /> Apply by email</a>
            {job.whatsapp && <a href={job.whatsapp} target="_blank" rel="noreferrer" className="btn-outline"><WhatsappLogo size={16} weight="fill" /> Ask on WhatsApp</a>}
          </div>
        </div>
      )}
    </article>
  );
}
